<template>
    <div class="create-appointment-process-modal">
        <ProcessModal
            data-qa="create-appointment-modal-process"
            :is-open="isOpen"
            :title="$t('funnel.edit.sideBar.create.appointment.title')"
            :summary-text="$t('funnel.edit.sideBar.create.appointment.summary')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <div class="vertical-container">
                    <div class="wrap-appointment-info-input">
                        <CampaignBuilderMergeableTextArea
                            v-model="appointment.title"
                            data-qa="create-task-title-input"
                            :has-error="isTitleEmpty"
                            :default-place-holder="!appointment.title ? $t('funnel.edit.sideBar.create.appointment.placeholder.title') : $t('funnel.edit.sideBar.create.appointment.error.saving.required')"
                            :has-legacy-format="true"
                            :funnel-id="funnelId"
                            :merge-field-categories="mergeFields"
                            class="create-appointment-merge-field-input"
                        />
                    </div>
                    <div class="wrap-appointment-info-input">
                        <CampaignBuilderMergeableTextArea
                            v-model="appointment.body"
                            data-qa="create-task-body-input"
                            :default-place-holder="$t('funnel.edit.sideBar.create.appointment.placeholder.body')"
                            :has-legacy-format="true"
                            :funnel-id="funnelId"
                            :merge-field-categories="mergeFields"
                            class="create-appointment-merge-field-input"
                        />
                    </div>
                    <div class="wrap-action-type-select" />
                    <DsMultiselect
                        v-model="selectedAppointmentType"
                        bind-value-only
                        label-prop="value"
                        value-prop="key"
                        required
                        class="appointment-types-select"
                        :placeholder="$t('funnel.edit.sideBar.create.appointment.label.action.type')"
                        :options="optionsAppointmentTypeItems"
                        :force-invalid="isAppointmentTypeEmpty"
                    >
                        <template #help>
                            <span v-if="isAppointmentTypeEmpty">
                                {{ $t('funnel.edit.sideBar.create.appointment.error.saving.appointment.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                    <DsCheckbox
                        v-model="checkboxAssignToOwner"
                        class="checkbox-assign-to-owner"
                        :label="$t('funnel.edit.sideBar.create.appointment.checkbox.label.assign.to.contacts.owner')"
                    />
                    <DsMultiselect
                        v-model="selectedBackupAssigned"
                        bind-value-only
                        label-prop="value"
                        value-prop="key"
                        required
                        class="contacts-to-backup-assigned"
                        :placeholder="$t('funnel.edit.sideBar.create.appointment.placeholder.assign.to.backup')"
                        :options="optionsBackupAssignToItems"
                        :force-invalid="isBackupAssignedEmpty"
                    >
                        <template #help>
                            <span v-if="isBackupAssignedEmpty">
                                {{ $t('funnel.edit.sideBar.create.appointment.error.saving.appointment.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                    <DsCheckbox
                        v-model="checkboxNotifyOwner"
                        class="checkbox-notify-owner"
                        :label="$t('funnel.edit.sideBar.create.appointment.label.notify.owner.when.appointment.created')"
                    />
                    <DsMultiselect
                        v-model="selectedNotifyId"
                        bind-value-only
                        label-prop="value"
                        value-prop="key"
                        class="notify-owner-appointment-users"
                        :placeholder="$t('funnel.edit.sideBar.create.appointment.placeholder.notify.users')"
                        :options="optionsNotifyIdsItems"
                    />
                    <div class="wrap-schedule-vertical-appointment">
                        <span class="schedule-appointment-span">
                            {{ $t('funnel.edit.sideBar.create.appointment.label.schedule.appointment') }}
                        </span>
                        <div class="wrap-schedule-horizontal-appointment-select-a-date">
                            <div class="wrap-select-day">
                                <DsMultiselect
                                    v-model="selectedDelayFldDate"
                                    bind-value-only
                                    label-prop="value"
                                    value-prop="key"
                                    class="select-date-assign-only"
                                    :placeholder="$t('funnel.edit.sideBar.create.appointment.placeholder.select.a.date')"
                                    :options="optionsSelectedDates"
                                />
                            </div>
                            <span class="span-at-label">
                                {{ $t('funnel.edit.sideBar.create.appointment.label.at') }}
                            </span>
                            <div class="input-time-specification">
                                <DsDatePicker
                                    v-model="selectedTimeSpecification"
                                    time-only
                                    name="time-basic"
                                    :label="$t('funnel.edit.sideBar.create.appointment.placeholder.time')"
                                />
                            </div>
                        </div>
                        <div class="wrap-schedule-horizontal-appointment-occurrence">
                            <div class="input-number-of-days">
                                <DsInputField
                                    v-model.number="numberOfDays"
                                />
                            </div>
                            <span class="label-days-between">
                                {{ $t('funnel.edit.sideBar.create.appointment.label.days') }}
                            </span>
                            <div class="select-period-time">
                                <DsMultiselect
                                    v-model="selectedBeforeAfterPeriod"
                                    bind-value-only
                                    label-prop="value"
                                    value-prop="key"
                                    class="select-before-after-period"
                                    :options="optionsBeforeAfterPeriods"
                                />
                            </div>
                            <DsMultiselect
                                v-model="selectedPeriodDetail"
                                bind-value-only
                                label-prop="value"
                                value-prop="key"
                                class="select-next-occurrence-duration"
                                :options="optionsOccurrencesYearItems"
                            />
                        </div>
                        <DsMultiselect
                            v-model="selectedDelayFieldDuration"
                            bind-value-only
                            label-prop="value"
                            value-prop="key"
                            class="select-delay-field-duration"
                            :placeholder="$t('funnel.edit.sideBar.create.appointment.label.appointment.duration')"
                            :options="optionsFieldDurationItems"
                        />
                    </div>
                </div>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import { create, edit, updateAppointment } from './create.appointment.api';
import CampaignBuilderMergeableTextArea from '../../../whens/campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';
import { getMergeFieldsForCB } from '../../../../configuration/task.flow.api';


export const DEFAULT_MILITARY_TIME = '0800';

export default {
    DEFAULT_MILITARY_TIME,
    components: {
        ProcessModal,
        CampaignBuilderMergeableTextArea,
    },
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    appointmentId: null,
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            mergeFields: [],
            appointment: {
                id: null,
                title: '',
                body: '',
            },
            checkboxAssignToOwner: true,
            checkboxNotifyOwner: true,
            selectedAppointmentType: null,
            selectedBackupAssigned: null,
            selectedNotifyId: null,
            selectedTimeSpecification: this.$options.DEFAULT_MILITARY_TIME,
            numberOfDays: 0,
            optionsSelectedDates: [],
            selectedDelayFldDate: null,
            optionsOccurrencesYearItems: [],
            selectedPeriodDetail: null,
            optionsBeforeAfterPeriods: [],
            selectedBeforeAfterPeriod: null,
            optionsFieldDurationItems: [],
            selectedDelayFieldDuration: null,
            optionsAppointmentTypeItems: [],
            optionsBackupAssignToItems: [],
            optionsNotifyIdsItems: [],
            isTitleEmpty: false,
            isAppointmentTypeEmpty: false,
            isBackupAssignedEmpty: false,
        };
    },
    async mounted() {
        await this.loadConfigAppointment();
        this.mergeFields = await getMergeFieldsForCB(this.funnelId);
    },
    methods: {
        async loadConfigAppointment() {
            let appointmentId = null;

            if (this.cell?.value?.appointmentId) {
                const { appointmentId: cellAppointmentId } = this.cell.value;

                appointmentId = parseInt(cellAppointmentId, 10);
            } else {
                appointmentId = await create();
            }

            this.appointment.id = appointmentId;

            const {
                appointmentType,
                appointmentTypeItems,
                allUsersMap,
                appointmentTitle,
                appointmentBody,
                assignToOwner,
                backupAssignTo,
                notifyOwner,
                notifyIds,
                appointmentDelayFieldsDto,
            } = await edit(appointmentId);

            this.appointment.title = appointmentTitle;
            this.appointment.body = appointmentBody;
            this.selectedAppointmentType = appointmentType;
            this.optionsAppointmentTypeItems = Object.entries(appointmentTypeItems).map(([key, value]) => ({
                key, value,
            }));
            this.checkboxAssignToOwner = assignToOwner;
            const allBackupUsersArray = Object.entries(allUsersMap).map(([key, value]) => ({
                key: parseInt(key, 10),
                value,
            }));

            const allNotifyIdsUsersArray = Object.entries(allUsersMap).map(([key, value]) => ({
                key,
                value,
            }));

            this.optionsBackupAssignToItems = allBackupUsersArray;
            this.selectedBackupAssigned = backupAssignTo;

            this.checkboxNotifyOwner = notifyOwner;
            this.selectedNotifyId = notifyIds;
            this.optionsNotifyIdsItems = allNotifyIdsUsersArray;

            const {
                marketingAppointment0DelayFld,
                marketingAppointment0DelayFldTime,
                delayFieldDays,
                delayFieldItems,
                delayFieldBeforeAfter,
                delayFieldBeforeAfterItems,
                delayFieldYearItems,
                delayFieldYear,
                delayFieldDurationItems,
                delayFieldDuration,
            } = appointmentDelayFieldsDto;

            this.optionsSelectedDates = Object.entries(delayFieldItems).map(([key, value]) => ({
                key, value,
            }));

            this.selectedDelayFldDate = marketingAppointment0DelayFld;
            this.selectedTimeSpecification = !marketingAppointment0DelayFldTime || marketingAppointment0DelayFldTime === '' ? this.$options.DEFAULT_MILITARY_TIME : marketingAppointment0DelayFldTime.replace(':', '');

            this.optionsFieldDurationItems = Object.entries(delayFieldDurationItems).map(([key, value]) => ({
                key: parseInt(key, 10),
                value,
            }));
            this.selectedDelayFieldDuration = delayFieldDuration;

            this.numberOfDays = delayFieldDays;

            this.optionsBeforeAfterPeriods = Object.entries(delayFieldBeforeAfterItems).map(([key, value]) => ({
                key: parseInt(key, 10),
                value,
            }));
            this.selectedBeforeAfterPeriod = delayFieldBeforeAfter;

            this.optionsOccurrencesYearItems = Object.entries(delayFieldYearItems).map(([key, value]) => ({
                key, value,
            }));
            this.selectedPeriodDetail = delayFieldYear;
        },
        closeModal() {
            this.$emit('close-modal');
        },
        async updateConfig() {
            try {
                const cell = { ...this.cell };

                this.isTitleEmpty = this.appointment?.title === null || this.appointment?.title?.trim().length === 0;
                this.isAppointmentTypeEmpty = this.selectedAppointmentType === null;
                this.isBackupAssignedEmpty = this.selectedBackupAssigned === 0 || this.selectedBackupAssigned === null;

                const showError = this.isTitleEmpty || this.isAppointmentTypeEmpty || this.isBackupAssignedEmpty;

                cell.value = cell.value || {};

                if (!showError) {
                    cell.value.appointmentId = this.appointment.id;
                    const payload = {
                        assignToOwner: this.checkboxAssignToOwner ? 1 : 0,
                        backupAssignTo: this.selectedBackupAssigned,
                        notifyIds: this.selectedNotifyId,
                        appointmentBody: this.appointment.body,
                        appointmentTitle: this.appointment.title,
                        appointmentType: this.selectedAppointmentType,
                        delayFld: this.selectedDelayFldDate,
                        delayFldYear: this.selectedPeriodDetail,
                        delayFldDays: this.numberOfDays,
                        delayFldTime: this.selectedTimeSpecification === '' || !this.selectedTimeSpecification ? '08:00' : `${this.selectedTimeSpecification.slice(0, 2)}:${this.selectedTimeSpecification.slice(2)}`,
                        delayFldBeforeAfter: this.selectedBeforeAfterPeriod,
                        delayFldDuration: this.selectedDelayFieldDuration,
                        notifyOwner: this.checkboxNotifyOwner ? 1 : 0,
                        published: false,
                    };

                    await updateAppointment(this.appointment.id, payload);

                    this.$emit('update-cell-value', { cell });
                }
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.sideBar.create.appointment.error.saving.appointment');

                this.$error({
                    message: errorMessage,
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.create-appointment-merge-field-input {
    width: px-to-rem(590);
}

.vertical-container {
    display: flex;
    flex-direction: column;
}

.wrap-appointment-info-input {
    display: grid;
    grid-template-columns: 92% 8%;
}

.schedule-appointment-span {
    font-weight: bold;
    margin-bottom: $spacing-100;
}

.wrap-schedule-vertical-appointment {
    display: flex;
    flex-direction: column;
}

.wrap-schedule-horizontal-appointment-select-a-date {
    display: flex;
    flex-direction: row;
    width: 80%;
}

.wrap-select-day {
    flex: 0 0 82%;
}

.select-date-assign-only {
    word-break: break-all;
}

.span-at-label {
    flex: 0 0 10%;
    margin: $spacing-125;
    max-width: $spacing-125;
    max-height: $spacing-250;
}

.input-time-specification {
    flex: 0 0 30%;
}

.wrap-schedule-horizontal-appointment-occurrence {
    display: grid;
    grid-template-columns: 10% 10% 20% 60%;
}

.input-number-of-days {
    flex: 0 0 10%;
}

.label-days-between {
    flex: 0 0 10%;
    margin: $spacing-125;
    max-width: px-to-rem(30);
    max-height: $spacing-250;
}

.select-period-time {
    flex: 0 0 20%;
    margin-right: $spacing-125;
}

.merge-icon {
    padding: $spacing-100 $spacing-100 $spacing-100 $spacing-200;
    width: px-to-rem(24);
    height: px-to-rem(24);
    color: $color-blue-700;
    pointer-events: none;
}

.checkbox-assign-to-owner, .checkbox-notify-owner {
    padding-top: 0;
}

</style>

<style lang="scss">
.create-appointment-process-modal {
    div.modal-body,
    div.multiselect-tags > span > label,
    div.input-time-specification > div > div > label  {
        padding-top: 0;
    }
}

.create-appointment-process-modal .multiselect .multiselect-single {
    overflow: hidden;
}
</style>
